import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { Configuration } from 'src/app/app.constants';
import { ObjectTypes } from '../../shared/enums/objectTypes';
import { Account } from '../../shared/models/account';
import { FilterViewModel } from '../../shared/models/filter/filterViewModel';
import { LightFilterUpdateModel } from '../../shared/models/filter/LightFilterUpdateModel';
import { ModifiableEntityViewModel } from '../../shared/viewModels/bases/modifiableEntityViewModel';
import { AccountHubDepartmentViewModel } from '../models/departments/accounthub-departments.model';
import { AccountHubDepartmentUpdateModel } from '../models/departments/accounthub-department-update.model';
import { AccountHubEmployeeViewModel } from '../models/employees/accounthub-employee.model';
import { AccountHubEmployeeUpdateModel } from '../models/employees/accountHub-employee-update.model';
import { AccountHubDepartmentCategoryViewModel } from '../models/departmentCategories/accounthub-department-categories.model';
import { AccountLocalisationViewModel } from '../../shared/viewModels/accountLocalisationViewModel';
import { AccountSettingViewModel } from '../../shared/viewModels/accountSettingViewModel';
import { HubUpdateGeneralSettingsViewModel } from '../models/settings/hub-update-general-settings.viewModel';
import { AccountSettingTypes } from '../../shared/enums/accountSettingTypes';
import { AccountDetailsViewModel } from '../../settings/viewModels/accountDetailsViewModel';
import { EnumBasedLocalisationViewModel } from '../../shared/viewModels/enumBasedLocalisationViewModel';
import { TaskAutoUpdateViewModel } from '../../settings/viewModels/taskAutoUpdateViewModel';
import { EmployeeViewModel } from '../../shared/viewModels/employeeViewModel';
import { ValidatedViewModel } from '../../shared/viewModels/validatedViewModel';
import { AccountHubChildAccountCardViewModel, AccountHubChildAccountDetailsViewModel } from '../models/hubAccounts/childAccountViewModels';

@Injectable({
  providedIn: 'root',
})
export class AccountHubService {

  constructor(private http: HttpClient, private configuration: Configuration) {
    
  }

  /**
   * Getter that dynamically builds the actionUrl each time.
   * This ensures the URL updates when the backend API changes (e.g., account switching).
   */
  private get actionUrl(): string {
    return this.configuration.buildEndpoint(`Hub/`);
  }

  updateObject(globalObjectId: number, accountID: number, objectType: ObjectTypes, changes: FilterViewModel[]): Observable<any> {
    const lightObject = new LightFilterUpdateModel();
    lightObject.globalObjectId = globalObjectId;
    lightObject.objectType = objectType;
    lightObject.accountId = accountID;
    lightObject.changes = changes;

    return this.http.post(`${this.actionUrl}UpdateLight`, lightObject);
  }

  getTable(objectType: ObjectTypes, filters: FilterViewModel[]): Observable<ModifiableEntityViewModel[]> {
    return this.http.post<ModifiableEntityViewModel[]>(`${this.actionUrl}table/${objectType}`, filters);
  }

  getHubChildEmployees(): Observable<EmployeeViewModel[]> {
    return this.http.get<EmployeeViewModel[]>(`${this.actionUrl}HubChildEmployees`);
  }

  getReleatedAccounts(): Observable<Account[]> {
    return this.http.get<Account[]>(`${this.actionUrl}accounts`);
  }

  getHubEmployees():Observable<AccountHubEmployeeViewModel[]>{
    return this.http.get<AccountHubEmployeeViewModel[]>(`${this.actionUrl}employees`);
  }

  shareHubEmployees(updateModel:AccountHubEmployeeUpdateModel[]):Observable<void>{
    return this.http.post(`${this.actionUrl}employees/share`,updateModel).pipe(
      map((res: any) => res),
      catchError((err) => throwError(err))
    );
  }

  getAllowedFilters(): Observable<FilterViewModel[]> {
    return this.http.get<FilterViewModel[]>(`${this.actionUrl}users/allowedFilters`);
  }

  /** Department Operations */
  addHubDepartment(department: AccountHubDepartmentViewModel):Observable<ValidatedViewModel>{
    return this.http.post<ValidatedViewModel>(this.actionUrl + `departments/add`, department);
  }

  updateHubDepartment(department: AccountHubDepartmentViewModel):Observable<ValidatedViewModel>{
    return this.http.post<ValidatedViewModel>(this.actionUrl + `departments/update`, department);
  }

  shareHubDepartment(updateModel:AccountHubDepartmentUpdateModel[]): Observable<any> {
    return this.http.post<ModifiableEntityViewModel[]>(`${this.actionUrl}departments/share`, updateModel);
  }

  getHubDepartmentCategories(): Observable<AccountHubDepartmentCategoryViewModel[]> {
    return this.http.get<AccountHubDepartmentCategoryViewModel[]>(`${this.actionUrl}departmentCategory`);
  }

  getHubDepartments(): Observable<AccountHubDepartmentViewModel[]> {
    return this.http.get<AccountHubDepartmentViewModel[]>(`${this.actionUrl}departments`);
  }

  /** */
  archive(objectType: ObjectTypes, objectId: number): Observable<any> {
    return this.http.post(`${this.actionUrl}archive/${objectType}/${objectId}`, {});
  }

  //* Get all account localizations for parent and child accounts */
  getParentChildAccountLocalizations(): Observable<AccountLocalisationViewModel[]> {
    return this.http.get<AccountLocalisationViewModel[]>(`${this.actionUrl}accounts/localisations`);
  }

  //** Share Account localizations with all child accounts */
  shareAccountLocalisations(isShared: boolean): Observable<void> {
    return this.http.post<void>(this.actionUrl + `accounts/localisations/share/${isShared}`, {});
  }

  shareIncidentSeverities():Observable<void>{
    return this.http.get<void>(this.actionUrl + `control/settings/incident-severities/share`);
  }

  /** Share Incident Categories  & Checklists */
  shareIncidentCategory(incidentCategoryId: number):Observable<void>{
    return this.http.get<void>(this.actionUrl + `control/settings/incident-category/share/${incidentCategoryId}`);
  }

  deleteIncidentCategory(incidentCategoryId: number):Observable<void>{
    return this.http.delete<void>(this.actionUrl + `control/settings/incident-category/delete/${incidentCategoryId}`);
  }

  shareChecklist(checklistId: number):Observable<void>{
    return this.http.get<void>(this.actionUrl + `control/settings/checklists/share/${checklistId}`);
  }

  /** Share account settings across all chlild accounts */
  shareAccountGeneralSettings(settingsList:AccountSettingViewModel[],isFileUploaded:boolean): Observable<void> {
    const updateModel:HubUpdateGeneralSettingsViewModel =  {
      AccountSettings:settingsList,
      isFileUploaded:isFileUploaded
    };

    return this.http.post<void>(this.actionUrl + `accounts/settings/share`,updateModel);
  }

  /** Get account settings for all chlild accounts   */
  getChildAccountSettingTypes(settingTypes:AccountSettingTypes[]): Observable<AccountSettingViewModel[]> {
    return this.http.post<AccountSettingViewModel[]>(this.actionUrl + `accounts/childs/settings`,settingTypes);
  }

  /** Share Account Security settings with all child accounts */
  shareAccountSecurity(accountDetails:AccountDetailsViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + `accounts/settings/security`,accountDetails);
  }

  /** Get account security settings for all chlild accounts */
  getAccountSecurity(): Observable<AccountDetailsViewModel[]> {
    return this.http.get<AccountDetailsViewModel[]>(this.actionUrl + `accounts/childs/settings/security/all`);
  }

  getHubEnumBasedLocalisations(filterType:number):Observable<EnumBasedLocalisationViewModel[]>{
    return this.http.get<EnumBasedLocalisationViewModel[]>(this.actionUrl + `accounts/childs/enumbased/${filterType}`);
  }

  /** Share Hub Account Details across all child accounts */
  shareAccountDetails(hubAccountDetails:AccountDetailsViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + `accounts/childs/settings/share`,hubAccountDetails);
  }

  /**
   * Get child Account brands
   */
  getChildAccountBrands():Observable<AccountDetailsViewModel[]>{
    return this.http.get<AccountDetailsViewModel[]>(this.actionUrl + `accounts/childs/brands`);
  }

  /**
   * Update child Account Details
   */
  updateChildAccountDetails(accountDetails: AccountHubChildAccountDetailsViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + `accounts/child/update`, accountDetails);
  }

  /**
   * Get child Account Details
   */
  getChildAccountDetails(accountId: number): Observable<AccountHubChildAccountDetailsViewModel> {
    return this.http.get<AccountHubChildAccountDetailsViewModel>(this.actionUrl + `accounts/child/details/${accountId}`);
  }

  /**
   * Get child Account Details
   */
  getChildAccounts(): Observable<AccountHubChildAccountCardViewModel[]> {
    return this.http.get<AccountHubChildAccountCardViewModel[]>(this.actionUrl + `accounts/child/list`);
  }

  shareAccountEnumBasedLocalisations(enumBasedLocalisation:EnumBasedLocalisationViewModel[]):Observable<void>{
    return this.http.post<void>(this.actionUrl + `accounts/childs/enumbased/share`,enumBasedLocalisation);
  }

  getPlanningTaskAutoRagUpdate():Observable<TaskAutoUpdateViewModel[]>{
    return this.http.get<TaskAutoUpdateViewModel[]>(this.actionUrl + `planning/settings/autorag`);
  }

  shareTaksRagAutoUpdate(model:TaskAutoUpdateViewModel[]):Observable<void>{
    return this.http.post<void>(this.actionUrl + `planning/settings/autorag/share`,model);
  }

  updateTaskRagAutoUpdate():Observable<void>{
    return this.http.post<void>(this.actionUrl + `planning/settings/autorag/refresh`,{});
  }

  updateBasicEmployeeDetails(employee:AccountHubEmployeeViewModel):Observable<void>{
    return this.http.post<void>(this.actionUrl + `employees/basic/update`,employee);
  }

  addBasicEmployeeDetails(employee:AccountHubEmployeeViewModel):Observable<AccountHubEmployeeViewModel>{
    return this.http.post<AccountHubEmployeeViewModel>(this.actionUrl + `employees/basic/add`,employee);
  }

  deleteHubEmpoloyee(employeeId:number):Observable<void>{
    return this.http.delete<void>(this.actionUrl + `employees/`+employeeId);
  }
}
